import { getXlsxSourceColumns } from '../../../../libs/excel/XlsxSourceColumns';

const LOCATION_FIELD_IDS = {
	city: ['cityId', 'city_id'],
	township: ['townshipId', 'township_id'],
	contractZone: ['contractZoneId'],
	orderZone: ['orderZoneId'],
	zone: ['zoneId', 'zone_id'],
};

const LOCATION_ZONE_FALLBACK_COLUMNS = ['Distrito', 'Zona', 'Zona de Trabajo'];

const normalizeHeader = value =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim()
		.toLowerCase();

const ASSIGNMENT_SOURCE_HEADERS = {
	name: new Set(['tecnico']),
	workCode: new Set(['identificador tecnico']),
	supervisorName: new Set(['supervisor']),
	supervisorWorkCode: new Set(['identificador supervisor']),
};

const hasOwn = (value, key) =>
	Object.prototype.hasOwnProperty.call(value || {}, key);

const isBlank = value => String(value ?? '').trim() === '';

const isLocationPlaceholder = value =>
	isBlank(value) || String(value).trim() === '95';

const isRawColoniaZoneField = field =>
	Object.values(LOCATION_FIELD_IDS)
		.reduce((ids, values) => ids.concat(values), [])
		.filter(id => /zone/i.test(id))
		.includes(field?.id) &&
	String(field?.name || '')
		.trim()
		.toLowerCase() === 'colonia';

const resolvePrimaryFieldValue = ({ order, field }) => {
	const sourceColumns = getXlsxSourceColumns(order);
	if (
		Array.isArray(sourceColumns) &&
		Number.isInteger(field?.sourceColumnIndex) &&
		field.sourceColumnIndex >= 0
	) {
		return {
			value: sourceColumns[field.sourceColumnIndex] ?? '',
			sourceFound: true,
		};
	}

	if (!field?.name) return { value: '', sourceFound: false };
	return {
		value: order[field.name],
		sourceFound: hasOwn(order, field.name),
	};
};

const resolveFieldValue = ({ order, field }) => {
	const primary = resolvePrimaryFieldValue({ order, field });
	const primaryValue = primary.value;
	if (isRawColoniaZoneField(field) && isLocationPlaceholder(primaryValue)) {
		const selectedColumn = LOCATION_ZONE_FALLBACK_COLUMNS.find(
			column => hasOwn(order, column) && !isLocationPlaceholder(order[column]),
		);
		if (selectedColumn) {
			return {
				value: order[selectedColumn],
				provenance: {
					primaryColumn: field.name,
					primaryValue,
					selectedColumn,
					selectedValue: order[selectedColumn],
					reason: 'location_placeholder_fallback',
				},
			};
		}
	}
	return {
		...primary,
		value: primaryValue || field.default || '',
		sourceValue: primaryValue,
	};
};

const getLocationKey = field =>
	Object.keys(LOCATION_FIELD_IDS).find(locationKey =>
		LOCATION_FIELD_IDS[locationKey].includes(field?.id),
	);

const getSourceLocationValues = ({ order, fields, fieldValues }) =>
	Object.keys(LOCATION_FIELD_IDS).reduce((sourceValues, locationKey) => {
		const field = fields.find(candidate =>
			LOCATION_FIELD_IDS[locationKey].includes(candidate.id),
		);
		const resolved = field ? fieldValues.get(field.id) : undefined;
		if (resolved?.sourceFound || resolved?.provenance) {
			// Keep the selected raw source cell exactly as provided. When a fallback is
			// used, sourceProvenance retains the original primary cell as well.
			sourceValues[locationKey] = resolved.value ?? order[field.name];
		}
		return sourceValues;
	}, {});

const getSourceAssignmentValues = ({ fields, fieldValues }) =>
	fields.reduce((source, field) => {
		const header = normalizeHeader(field?.name);
		const key = field?.id === 'assignedTechWorkCode'
			? 'workCode'
			: Object.keys(ASSIGNMENT_SOURCE_HEADERS).find(candidate =>
				ASSIGNMENT_SOURCE_HEADERS[candidate].has(header),
			);
		const resolved = fieldValues.get(field.id);
		if (key && resolved?.sourceFound)
			source[key] = key === 'workCode' ? resolved.sourceValue : resolved.value;
		return source;
	}, {});

export default class XlsxScrapingProcess {
	constructor(order, fields, { rowNumber } = {}) {
		this.order = order;
		this.fields = fields;
		this.rowNumber = rowNumber;
	}

	run() {
		const clientRowId = this.rowNumber
			? `import-row-${this.rowNumber}`
			: undefined;
		const fieldValues = new Map();
		const scrapedOrder = this.fields.reduce((_order, field) => {
			const resolved = resolveFieldValue({
				order: this.order,
				field,
			});
			fieldValues.set(field.id, resolved);
			_order[field.id] = String(resolved.value ?? '').trim();

			return _order;
		}, {});

		const sourceValues = getSourceLocationValues({
			order: this.order,
			fields: this.fields,
			fieldValues,
		});
		const assignmentSource = getSourceAssignmentValues({
			fields: this.fields,
			fieldValues,
		});
		if (
			Object.keys(sourceValues).length ||
			Object.keys(assignmentSource).length ||
			clientRowId
		) {
			const sourceProvenance = this.fields.reduce((provenance, field) => {
				const locationKey = getLocationKey(field);
				const fieldProvenance = fieldValues.get(field.id)?.provenance;
				if (locationKey && fieldProvenance)
					provenance[locationKey] = fieldProvenance;
				return provenance;
			}, {});
			scrapedOrder.importDiagnostics = {
				rowNumber: this.rowNumber,
				...(clientRowId ? { clientRowId } : {}),
				...(Object.keys(sourceValues).length ? { sourceValues } : {}),
				...(Object.keys(sourceProvenance).length ? { sourceProvenance } : {}),
				...(Object.keys(assignmentSource).length
					? { assignment: { source: assignmentSource } }
					: {}),
			};
		}
		if (clientRowId) scrapedOrder.__importRowId = clientRowId;

		return scrapedOrder;
	}
}
